import React, { useEffect, useCallback } from 'react';
import { Platform, View, ActivityIndicator, StyleSheet } from 'react-native';
import { StatusBar as ExpoStatusBar } from 'expo-status-bar';
import * as ExpoLinking from 'expo-linking';
import { NavigationContainer, LinkingOptions, DefaultTheme, DarkTheme, getStateFromPath } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { initialWindowMetrics, SafeAreaProvider } from 'react-native-safe-area-context';
import { I18nextProvider } from 'react-i18next';
import i18n from './src/i18n';
import { documentTitle } from './src/i18n/routeTitles';
import { AuthProvider, useAuth } from './src/context/AuthContext';
import { useShareLinkAttribution } from './src/hooks/useShareLinkAttribution';
import { useAcquisitionAttribution } from './src/hooks/useAcquisitionAttribution';
import { useMarketingTags } from './src/hooks/useMarketingTags';
import { marketingTags } from './src/services/marketingTagRuntime';
import { CookieBanner } from './src/components/CookieBanner';
import { CookiePreferencesModal } from './src/components/CookiePreferencesModal';
import { applyWebTextDefaultFont } from './src/styles/webTextDefaultFont';
import { ConsentGate } from './src/components/consent/ConsentGate';
import { useCookieConsentSync } from './src/hooks/useCookieConsentSync';
import { useAppOpenedBeacon } from './src/hooks/useAppOpenedBeacon';
import { useLanguageSwitcherGate } from './src/hooks/useLanguageSwitcherGate';
import { setPendingRoute } from './src/navigation/pendingRoute';
import { resolveGuestRouteAccess } from './src/navigation/guestLiveAccess';
import { ImpersonationBanner } from './src/components/ImpersonationBanner';
import { AuthCTAProvider } from './src/context/AuthCTAContext';
import { ReportProvider } from './src/context/ReportContext';
import { RepostProvider } from './src/context/RepostContext';
import { ThemeProvider, useTheme } from './src/context/ThemeContext';
import { useAgapellaFonts } from './src/styles/useAgapellaFonts';
import { FundProvider } from './src/context/FundContext';
import { ChatContactsProvider } from './src/context/ChatContactsContext';
import { MessageNotifier } from './src/components/MessageNotifier';
import { FlashNoticeHost } from './src/components/FlashNoticeHost';
import { PushNotifications } from './src/components/PushNotifications';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { CalendarProvider } from './src/context/CalendarContext';
import { BlockedUsersProvider } from './src/context/BlockedUsersContext';
import { EventsFilterProvider } from './src/context/EventsFilterContext';
import { SavedEventsProvider } from './src/context/SavedEventsContext';
// CORE-638 — only the entry screens are static; every other route is an async
// chunk from ./src/screens/lazy, pulled on first visit behind the Suspense
// boundary below. Importing them here instead would put the whole app in the
// first chunk, which is exactly what this ticket is about.
// Straight at the modules, NOT at ./src/screens: that barrel re-exports all 58
// screens statically, so touching it would drag the whole app back into the
// first chunk and undo the splitting below.
import { HomeScreen } from './src/screens/HomeScreen';
import { LoginScreen } from './src/screens/LoginScreen';
// CORE-1207 — keep the offline-event creator eager. Its route chunk could
// finish downloading without resolving React.lazy, leaving /events/create on
// the global loader forever. The heavy wizard is already reachable from the
// dashboard bundle, so registering this small gate eagerly avoids the broken
// async boundary without adding another copy of the wizard to the bundle.
import { CreateEventScreen } from './src/screens/CreateEventScreen';
import {
  RegisterScreen, ConfirmEmailScreen, ForgotPasswordScreen, ResetPasswordScreen, DashboardScreen,
  MastersScreen, MasterDetailScreen, MasterSubscriptionsScreen, AgapeCenterScreen, LivesScreen,
  PracticesScreen, PracticeDetailScreen, UploadPracticeScreen, MyCollectionScreen, SettingsScreen,
  LiveBroadcastScreen, AboutScreen, AboutPlatformScreen, ContactScreen, DownloadScreen,
  NotificationSettingsScreen, ChatsScreen, CommunityScreen, CircleChatScreen, FundScreen,
  FundDistributionScreen, HowItWorksScreen, FundTransparencyScreen, ReferralCreditsScreen,
  MasterWalletScreen, FundOnboardingScreen, DocsScreen, LegalDocScreen, PayoutDetailsScreen, FinanceDocumentsScreen,
  PracticeRoomsScreen, LiveRoomScreen, CustDevScreen, SoundLibraryScreen, MyCalendarScreen,
  MyLibraryScreen, MemoryFactsScreen, RoomAnnouncementScreen, AgapePracticeScreen,
  PublicUserProfileScreen, EventThreadScreen, PracticeDiscussionScreen, FeedScreen, GuestRoomScreen, SecuritySettingsScreen,
  EventsCalendarScreen, EventsAllScreen, EventsFiltersScreen, EventDetailScreen,
  EventParticipantsScreen, ChatThreadScreen, BlockedUsersScreen, CreateLiveSessionScreen,
  LiveSessionEntryScreen, EnvironmentScreen, SupportScreen, GamificationPreviewScreen,
  ProgramPurchaseScreen, ProgramPageScreen, AgapellaSubscriptionScreen,
  NewsScreen, NewsArticleScreen,
} from './src/screens/lazy';
import MasterSalesScreen from './src/screens/MasterSalesScreen';
import { FinanceScreen } from './src/screens/FinanceScreen';
import MasterAnalyticsScreen from './src/screens/MasterAnalyticsScreen';
import NotificationsScreen from './src/screens/NotificationsScreen';
import { LiveDetailRoute } from './src/screens/LiveDetailRoute';
import { GamificationShareLinkRoute } from './src/screens/GamificationShareLinkRoute';
import { LandingScreen } from './src/components/landing';
// CORE-1156 — посадочная реферальной ссылки грузится сразу, не отдельным чанком:
// это ПЕРВЫЙ экран пришедшего по ссылке гостя, и лишний round-trip за чанком —
// это лишний шанс, что человек уйдёт раньше, чем переход будет засчитан.
import { ReferralLandingScreen } from './src/screens/ReferralLandingScreen';
import { RootStackParamList } from './src/navigation/types';
import { navigationRef } from './src/navigation/rootNavigation';
import { noteRoute } from './src/services/chat/pageContext';
import { resolvePathAlias } from './src/navigation/pathAliases';
import { withHomeFallback } from './src/navigation/unknownPathFallback';
import { CIRCLES_ENABLED, ONBOARDING_TOUR_ENABLED, AGAPELLA_WIDGET_ENABLED, MONEY_SURFACE_ENABLED, PROGRAMS_SURFACE_ENABLED } from './src/config/flags';
import { resolveSentryEnvironment, testBuildSentryEnvironment, type SentryEnvironment } from './src/config/sentryEnvironment';
import { activeEnv, isEnvSwitchEnabled, loadEnvOverride } from './src/config/envSwitch';
import { resolveSentryReplayConfig } from './src/config/sentryReplay';
import { scrubSentryEvent, scrubSentryBreadcrumb } from './src/utils/sentryScrub';
import { throttleSentryEvent } from './src/utils/sentryBurst';
import { dropOrphanedAgoraAbort } from './src/services/rtc/publicationAbort';
import { dropForeignDescriptorRecursion } from './src/utils/sentryForeignScript';
import { BUILD_SHA } from './src/config/buildStamp';
import { OnboardingProfileGate } from './src/components/onboarding/OnboardingProfileGate';
import { AgapellaPaywallHost } from './src/components/subscription/AgapellaPaywall';
import { ChatImageViewerHost } from './src/components/room/ChatImageViewerHost';
import { AgapellaTrialGate } from './src/components/subscription/AgapellaTrialGate';
import { OnboardingTourProvider } from './src/onboarding/OnboardingTourProvider';
import { GamificationCelebrationProvider } from './src/components/gamification/CelebrationContext';
import { GamificationCelebrationHost } from './src/components/gamification/GamificationCelebrationHost';
import { OnboardingTourOverlay } from './src/onboarding/OnboardingTourOverlay';
import { LiveSessionProvider } from './src/live-session/LiveSessionContext';
import { LiveSessionHost } from './src/live-session/LiveSessionHost';
import { ReflectionLauncherProvider } from './src/components/reflection/ReflectionLauncherProvider';
import { AgapellaWidget } from './src/widget/AgapellaWidget';
import { ErrorBoundary } from './src/components/ui';
import { GlobalPageLoader } from './src/components/ui/GlobalPageLoader';
import { StaleBundleBanner } from './src/components/StaleBundleBanner';
import { SessionExpiredPrompt } from './src/components/SessionExpiredPrompt';
import * as Sentry from '@sentry/react-native';
import { installApiFallback } from './src/services/apiFallback';
import { initApiEndpoint } from './src/services/apiEndpoint';
import { installNativeApiFallback } from './src/services/nativeApiFallback';
import EnvSwitcher from './src/components/dev/EnvSwitcher';
import { installAriaHiddenFocusGuard } from './src/utils/ariaHiddenFocusGuard';
import { installStaleBundleGuard } from './src/utils/staleBundle';
import { installViewportResizeSync } from './src/utils/viewportResizeSync';
import { installFocusSource } from './src/utils/focusSource';
import { installRtcProxyConsole } from './src/services/rtc/rtcProxy';
import { installNoopReconfigurationGuard } from './src/services/rtc/noopReconfiguration';

// Web-only fetch interceptor. If a request to api.agapella.io throws a
// network-level TypeError, retries once against api-ru.agapella.io (our
// Russian edge). After 3 consecutive primary failures, switches sticky
// for 30 min. Native and non-prod hosts are no-op.
installApiFallback();

// CORE-1528: the same safety net on native — a stalled / failed direct call is
// retried via the Moscow edge, and a proven stall switches RU-first on. Web is
// a no-op (it has apiFallback above). Must precede initApiEndpoint so its
// rewrite wraps this one.
installNativeApiFallback();

// CORE-45: RU-first endpoint resolution (native + web). Probes the RU edge's
// /healthz on startup; if reachable, routes api.agapella.io → api-ru for REST
// and SignalR hubs so RU users load content instead of hanging. Installed AFTER
// installApiFallback so a rewritten api-ru URL passes through its primary check.
// Non-prod builds are inert.
initApiEndpoint();

// Web-only a11y guard: when React Navigation / a Modal sets aria-hidden on a
// background container that still holds the focused button, blur it so focus
// doesn't stay trapped in an aria-hidden subtree (silences the "Blocked
// aria-hidden … retained focus" warning). No-op on native.
installAriaHiddenFocusGuard();

// CORE-714: web-only net for «this tab predates the deploy». Catches the chunk
// failures `safeImport` can't see (Metro's async-require for lazy routes) and
// polls the shell on tab-focus, so the StaleBundleBanner offers «Обновить»
// instead of the user staring at a screen that never loads. No-op on native.
installStaleBundleGuard();

// CORE-932: web-only. react-native-web's Dimensions listens for size changes
// ONLY on window.visualViewport when it exists (Chrome), and a plain
// window-edge drag doesn't reliably fire that event — so useWindowDimensions
// goes stale and the layout stops following the window (the Live room, locked
// to 100dvh + overflow:hidden, then just clips a fixed-size render). This
// forwards the always-reliable window `resize` onto the visual viewport so
// RN-web re-measures. No-op on native.
installViewportResizeSync();

// CORE-1733: web-only. Marks <html data-focus-source="keyboard|pointer"> so the
// keyboard focus ring in public/global.css can light text fields for Tab but
// not for a mouse click. No-op on native (no document).
if (Platform.OS === 'web') installFocusSource();

// Sentry — error + crash reporting for web + iOS + Android. DSN is public
// (per the Sentry threat model — it's a write-only ingestion key); it lives
// in .env.development / .env.production as EXPO_PUBLIC_SENTRY_DSN. Init runs
// once at module load before any UI mounts so early-boot errors are captured.
//
// Source maps on web are uploaded by `npm run sentry:sourcemaps` (called from
// vercel.json's buildCommand). On native (EAS), the @sentry/react-native/expo
// config plugin handles upload automatically using SENTRY_AUTH_TOKEN from EAS
// secrets.
const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN;
// CORE-308 — release + environment so every event answers «какая версия / какое
// окружение». `EXPO_PUBLIC_COMMIT_SHA` is baked at build time by
// scripts/inject-build-env.js (Vercel git SHA); it's also the id the web
// source-map upload associates with, so the dashboard "Release" links straight
// to the un-minified frames. Falls back to undefined locally (no SHA) → SDK
// default. Web only — CORE-1391: EAS stamps the SHA too, but the native build
// uploads its source maps under `bundleId@version+build`, and a native event
// tagged with the SHA never met them. Native keeps the SDK default, which is
// exactly that id.
// BUILD_SHA first: it comes from a GENERATED source file, so metro's cache key
// tracks it. `EXPO_PUBLIC_COMMIT_SHA` is inlined by babel but is NOT part of that
// cache key, so on our shared-metro-cache CI runners it can carry a previous
// build's SHA. Env var kept as the fallback for EAS, which stamps it itself.
const SENTRY_RELEASE = BUILD_SHA || process.env.EXPO_PUBLIC_COMMIT_SHA || undefined;
// production = app.agapella.io; development = the shared dev deploy
// (rn-agape.vercel.app, fed from the `dev` branch); preview = per-PR Vercel
// preview URLs. There is no separate FE "stage" deploy today.
//
// On web, read the SERVING HOSTNAME rather than the baked EXPO_PUBLIC_WEB_URL:
// the dev/preprod stands build in production mode, so they bake
// `agapella.io` and used to report themselves as `production`. Native has no
// hostname and keeps the baked URL. See config/sentryEnvironment.ts.
const SENTRY_ENVIRONMENT = resolveSentryEnvironment(
  Platform.OS === 'web' && typeof window !== 'undefined'
    ? window.location.hostname
    : process.env.EXPO_PUBLIC_WEB_URL,
);
// CORE-1420 — `agapeRtcProxy.force()` in the browser console on dev/staging:
// pushes Agora through the Moscow fallback proxy for QA. Never on agapella.io.
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  installRtcProxyConsole(window.location.hostname);
  // CORE-2001 — Chrome 155 (macOS) refuses Agora's no-op setConfiguration and
  // the join hangs; must be in place before the SDK builds its first connection.
  installNoopReconfigurationGuard();
}
// CORE-979 — Session Replay on WEB, dev/staging only (never prod). Gated by
// `environment` inside init so prod can't be flipped on by a later release.
// Native is out of scope: the rates are only spread into init when enabled, so
// @sentry/react-native never auto-attaches its mobile replay integration.
const SENTRY_REPLAY = resolveSentryReplayConfig(Platform.OS, SENTRY_ENVIRONMENT);
function startSentry(environment: SentryEnvironment): void {
  if (!SENTRY_DSN) return;
  Sentry.init({
    dsn: SENTRY_DSN,
    release: Platform.OS === 'web' ? SENTRY_RELEASE : undefined,
    // Disable in __DEV__ so dev work doesn't pollute the prod issue feed.
    // Comment this out to test integration end-to-end from a dev build.
    enabled: !__DEV__,
    // Tag every event with the build environment so the Sentry dashboard
    // can split prod traffic from dev / preview deploys.
    environment,
    // CORE-1200 — веб шлёт события через свой домен (`api/sentry-tunnel.js`).
    // Прямой ingest `*.ingest.sentry.io` стоит в списках блокировщиков и
    // «privacy»-расширений: запрос режется до сети, SDK считает событие
    // отправленным, а в Sentry его нет — ровно этот пробел и завёл тикет.
    // Только web: тоннель — это функция Vercel, на нативе её нет, а расширений,
    // которые режут ingest, там не бывает.
    // CORE-632 — и только для DSN Sentry: свой GlitchTip (errors.agapella.io) в
    // эти списки не входит и отвечает браузеру по CORS сам, а стенд staging
    // отдаёт статику своим nginx — функции тоннеля там нет вовсе.
    ...(Platform.OS === 'web' && SENTRY_DSN.includes('.sentry.io') ? { tunnel: '/api/sentry-tunnel' } : {}),
    // Capture 10% of transactions in prod for perf monitoring; bumps to
    // 100% on previews so QA can read every trace. Bump down later if the
    // org plan starts hitting quota.
    tracesSampleRate: process.env.EXPO_PUBLIC_WEB_URL?.includes('agapella.io') ? 0.1 : 1.0,
    sendDefaultPii: false,
    // CORE-979 — Session Replay (web, dev/staging only). browserReplayIntegration
    // is NOT a default integration and must be added by hand. Masking is left on
    // by default AND pinned explicitly: maskAllText + maskAllInputs mean the
    // login/email/password fields are never captured, even on the dev stand.
    // blockAllMedia keeps video/img elements out of the recording. The sample
    // rates are spread in ONLY when enabled — a numeric rate on native would make
    // the SDK auto-attach mobile replay, which the ticket forbids.
    ...(SENTRY_REPLAY.enabled
      ? {
          integrations: [
            Sentry.browserReplayIntegration({
              maskAllText: true,
              maskAllInputs: true,
              blockAllMedia: true,
            }),
          ],
          replaysSessionSampleRate: SENTRY_REPLAY.replaysSessionSampleRate,
          replaysOnErrorSampleRate: SENTRY_REPLAY.replaysOnErrorSampleRate,
        }
      : {}),
    // Third-party console noise. Radix UI is NOT a dependency of this app;
    // the "DialogContent requires a DialogTitle" / "Missing `Description`"
    // a11y warnings come from the Vercel Toolbar (Vercel Live comments +
    // feedback), which Vercel edge-injects only on preview / *.vercel.app
    // deployments — never on agapella.io prod. Sentry's default Breadcrumbs
    // integration patches console.*, so that injected script's warnings get
    // recorded as breadcrumbs and ride along on every real error. Drop them
    // at the breadcrumb stage; denyUrls/ignoreErrors are belt-and-suspenders
    // in case the toolbar ever throws (or CaptureConsole gets added later).
    denyUrls: [/vercel\.live/i, /vercel-scripts\.com/i, /\/_next-live\//i],
    ignoreErrors: [
      /DialogContent.+requires.+DialogTitle/i,
      /Missing `?Description`? or `?aria-describedby/i,
      // CORE-1165 — "The play() request was interrupted by a call to pause()"
      // and its twin "...by a new load request": a DOMException AbortError that
      // lands in onunhandledrejection with NO stacktrace, because the media
      // element rejected the promise, not a frame of ours (REACT-NATIVE-40, the
      // Live-session page). The interruption is by definition our own — the
      // pause() / src swap the player performs on purpose — and there is nothing
      // to act on: useRoomMediaPlayer classifies AbortError and re-arms the start
      // on `canplay` (CORE-1019), LivePracticeOverlay waits for readiness instead
      // of racing the load, and the bare element.play() calls left without a
      // catch are inside the SDK (agora-rtc-sdk-ng), which we cannot wrap.
      // A start that genuinely never happened is still visible — through our own
      // analytics (room_media_autoplay_blocked / live_practice_autoplay_blocked),
      // which carry the rejection `reason` as a field.
      /play\(\) request was interrupted/i,
    ],
    // CORE-1059 — the SignalR JWT rides in the query of every hub URL
    // (`/hubs/*?access_token=<jwt>`) because a WS upgrade can't carry an
    // Authorization header. On a connection error the SDK captures that URL on
    // the event's request/exception, so scrub the query value before the event
    // leaves the browser — the query twin of the nginx access-log redaction and
    // of CORE-1038's .NET SecretScrubber.
    // CORE-1494 — порядок вызовов здесь строгий, и поменять его нельзя.
    // Скраб обязан отработать на КАЖДОМ событии: ограничитель залпа может
    // вернуть null, и если он идёт первым, то событие, которое он придержал,
    // мимо скраба не пройдёт — но то, которое он пропустит, уйдёт с живым JWT
    // в URL хаба. Поэтому сначала чистим секреты, потом решаем, отправлять ли:
    // решение об отправке не имеет права влиять на очистку.
    //
    // Сам ограничитель — ответ на REACT-NATIVE-2M: 14 867 событий «Handshake
    // was canceled», 99,2 % из них от ДВУХ застрявших хабов (10 555 и 4 193
    // события), по нескольку в секунду. За месяц эти два хаба дали ~97 % всех
    // прод-ошибок и вытеснили из Sentry остальное. Ни sampleRate, ни
    // дедупликации у ошибок тут нет (бэкенд шлёт 10 %, клиент — 100 %),
    // поэтому режем именно повтор, а не класс ошибок: см. sentryBurst.ts.
    //
    // CORE-1900 — между скрабом и ограничителем выбрасывается один вид событий:
    // НЕобработанный `AgoraRTCError WS_ABORT` / `OPERATION_ABORTED`. Это
    // осиротевшая копия отказа, который мы уже поймали: SDK (4.24) делает
    // `throw gen.throw(e), e`, и промис `gen.throw` не держит никто. Сам отказ
    // в телеметрии есть дважды — классифицированной строкой
    // (`room_rtc.publication_restore_deferred` и т. п.) и
    // `rtc_js_unhandled_rejection`. Обработанные события и любые другие коды
    // Agora (INVALID_OPERATION тоже) проходят как раньше.
    // Фильтр стоит после скраба и до ограничителя, чтобы выброшенное событие не
    // занимало место в залпе.
    //
    // CORE-2052 — там же выбрасывается переполнение стека из чужого
    // внедрённого скрипта (подменённый `_getOwnPropertyDescriptor`, все кадры
    // `<anonymous>`): см. sentryForeignScript.ts.
    beforeSend: (event) => throttleSentryEvent(dropForeignDescriptorRecursion(dropOrphanedAgoraAbort(scrubSentryEvent(event)))),
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.category === 'console') {
        const msg = typeof breadcrumb.message === 'string' ? breadcrumb.message : '';
        // The Radix help URL is a stable, narrowly-scoped signal that this
        // is the injected-toolbar a11y warning and nothing of ours.
        if (
          /radix-ui\.com\/primitives\/docs\/components\/dialog/i.test(msg) ||
          /DialogContent.+requires.+DialogTitle/i.test(msg) ||
          /Missing `?Description`? or `?aria-describedby/i.test(msg)
        ) {
          return null;
        }
      }
      // Fetch/xhr/http breadcrumbs carry the hub URL in data.url — redact the
      // access_token there too (CORE-1059).
      return scrubSentryBreadcrumb(breadcrumb);
    },
  });
}
// CORE-1391 — a native test build waits for the saved env choice (one storage
// read) so its events and sessions carry the env it really talks to. Errors in
// those first milliseconds go unreported there. Store builds and web start
// right away, as before.
if (Platform.OS !== 'web' && isEnvSwitchEnabled()) {
  void loadEnvOverride().then(() => startSentry(testBuildSentryEnvironment(activeEnv())));
} else {
  startSentry(SENTRY_ENVIRONMENT);
}


// Inject global styles for web
if (Platform.OS === 'web') {
  // Load global.css. In dev, bust the browser cache with a per-load version so
  // edits to this static public file always take effect on reload — a plain
  // `/global.css` <link> gets served stale otherwise (this silently ate several
  // CORE-639 CSS changes). Prod keeps the clean, cacheable URL.
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = __DEV__ ? `/global.css?v=${Date.now()}` : '/global.css';
  document.head.appendChild(link);

  // Load Stream Chat CSS
  const streamLink = document.createElement('link');
  streamLink.rel = 'stylesheet';
  streamLink.href = '/stream-chat.css';
  document.head.appendChild(streamLink);

  // Load Stream Chat style overrides (extracted from StreamChatStyles.ts)
  const overridesLink = document.createElement('link');
  overridesLink.rel = 'stylesheet';
  overridesLink.href = '/stream-chat-overrides.css';
  document.head.appendChild(overridesLink);

  // Load Stream Chat moderation overrides
  const moderationLink = document.createElement('link');
  moderationLink.rel = 'stylesheet';
  moderationLink.href = '/stream-chat-moderation.css';
  document.head.appendChild(moderationLink);

  // The approved @font-face declarations (Lora + Nunito Sans) live in
  // global.css; no CDN import here. The base reset stays inline so it applies
  // before the stylesheet finishes loading (prevents body shift / FOUC).
  //
  // CORE-1118: this block is appended to <head> AFTER the global.css <link>,
  // so on equal specificity it wins — it used to pin the whole document to
  // 'Inter', a family the app no longer ships, and that silently beat the
  // --ag-font-ui base in global.css. The literal Nunito Sans stack (not the
  // variable) is deliberate: this rule has to hold in the window before
  // global.css lands and defines --ag-font-ui.
  //
  // Inheritance alone does not reach <Text>: RN-Web pins its base class to the
  // system stack, so that rule is rewritten in RN-Web's own sheet (see
  // webTextDefaultFont.ts for why no stylesheet selector can do it).
  applyWebTextDefaultFont();

  const style = document.createElement('style');
  style.textContent = `
    * {
      box-sizing: border-box;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    html, body {
      margin: 0;
      padding: 0;
      font-family: 'Nunito Sans', 'Helvetica Neue', Arial, sans-serif;
      font-synthesis: none;
      overflow-x: hidden;
      box-shadow: none !important;
    }

    ::selection {
      background-color: rgba(255, 112, 32, 0.2);
      color: #FF7020;
    }

    /*
     * The rule above paints selected text brand-orange, which works on every
     * surface except the one that IS brand-orange: your own chat
     * bubble. There the white text turned orange on an orange background and
     * the selection read as a blank gap — you could not see what you had
     * selected in your own message.
     *
     * Selection has to be legible on the surface it lands on, so on that bubble
     * it flips: the text stays white and the background darkens. Both selectors
     * are needed — the bubble is the marked element, the text sits in a child.
     *
     * Scoped deliberately. Making ::selection surface-agnostic globally would
     * mean dropping the brand colour everywhere else, and it is right in the
     * other 99% of the app.
     */
    [data-own-bubble] ::selection,
    [data-own-bubble]::selection {
      background-color: rgba(0, 0, 0, 0.45);
      color: #fff;
    }

    /*
     * Same bubble, dark theme. The rule above was silently dead there: the
     * themed selection in public/global.css ([data-theme="dark"] ::selection)
     * carries !important, so the brand orange came back and your own message
     * went blank again on selection — the very bug that was fixed for light.
     *
     * The !important here is not decoration: it is what it takes to answer an
     * !important rule. The higher specificity then settles which of the two
     * wins, so this stays scoped to the one surface and the themed selection is
     * untouched everywhere else.
     *
     * The overlay is a touch deeper than in light: the bubble sits on a dark
     * page, and 0.45 read as a smudge rather than a selection.
     */
    [data-theme="dark"] [data-own-bubble] ::selection,
    [data-theme="dark"] [data-own-bubble]::selection {
      background-color: rgba(0, 0, 0, 0.55) !important;
      color: #fff !important;
    }
  `;
  document.head.appendChild(style);

  // Update document title and meta. This is the pre-navigation title; once the
  // NavigationContainer mounts, `documentTitle` below takes over per route.
  document.title = i18n.t('pageTitle.default');

  // Add meta description
  let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    document.head.appendChild(metaDesc);
  }
  metaDesc.content = 'Agapella brings together trusted masters, live practices, conscious events, personal recommendations and a supportive community for inner growth.';

  // Add viewport meta
  let viewportMeta = document.querySelector('meta[name="viewport"]');
  if (!viewportMeta) {
    viewportMeta = document.createElement('meta');
    viewportMeta.setAttribute('name', 'viewport');
    document.head.appendChild(viewportMeta);
  }
  viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');

  // CORE-1733 (возврат QA 28.10) — data-theme до первого кадра ставит скрипт в
  // public/index.html, после монтирования — ThemeContext; оба по одному правилу
  // (нет записи — light). Здесь стоял третий установщик с `|| 'system'`: при
  // выполнении бандла он ставил новому посетителю с тёмной ОС dark, и через
  // 10–50 мс ThemeContext возвращал light — 2–3 кадра графита в 15 из 16 загрузок.

  // Register service worker for push notifications
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => { });
  }

  // Link PWA manifest
  const manifestLink = document.createElement('link');
  manifestLink.rel = 'manifest';
  manifestLink.href = '/manifest.json';
  document.head.appendChild(manifestLink);
}

const Stack = createNativeStackNavigator<RootStackParamList>();

// Deep linking configuration for web URLs
const linking: LinkingOptions<RootStackParamList> = {
  prefixes: [
    // Native app scheme — enables agapella:// deep links (rooms, login, invites)
    // to route into screens on standalone/dev builds. Without these, custom-scheme
    // links are delivered to the app but React Navigation drops them (no matching
    // prefix), so the app just sits on Home. createURL('/') covers the standalone
    // `agapella://` and the Expo Go `exp://…/--/` forms; the literal is a belt-and-braces.
    ExpoLinking.createURL('/'),
    'agapella://',
    // Production domains
    // CORE-1187 — боевой домен здесь отсутствовал: ссылка вида
    // https://agapella.io/live/123 доходила до приложения (Android App Links),
    // но React Navigation не находила совпадающий префикс и высаживала
    // человека на Home вместо экрана Live. `app.agapella.io` — тот же сайт
    // за RU-edge, ссылки оттуда приходят с этим хостом.
    'https://agapella.io',
    'https://app.agapella.io',
    // RU-зеркало: кнопки «Поделиться» у людей из РФ отдают ссылки с этим
    // хостом. В App Links его нет — nginx отдаёт `/.well-known/` только
    // 308-м на agapella.io, а по редиректу подлинность не проверяют ни
    // Android, ни Apple. Префикс всё равно нужен: если такая ссылка дойдёт
    // до приложения, она должна найти экран, а не высадить на Home.
    'https://app-ru.agapella.io',
    'https://rn-agape.vercel.app',
    // Дев-стенд для РФ (CORE-1422): «Поделиться» на нём пишет ссылки с этим
    // хостом, QA проверяет переход в приложение именно по ним.
    'https://dev.agdev.tmless.org',
    'https://agape.com',
    // Dynamic origin for Vercel preview environments
    ...(Platform.OS === 'web' && typeof window !== 'undefined' ? [window.location.origin] : []),
    // Development
    'http://localhost:8081',
    'http://localhost:19006',
  ],
  config: {
    screens: {
      Home: '',
      News: {
        path: 'blog',
        parse: {
          category: (value: string) => value || undefined,
          kind: (value: string) => value === 'guide' ? 'guide' : value === 'article' ? 'article' : undefined,
          layoutPreview: (value: string) => value || undefined,
        },
      },
      NewsArticle: {
        path: 'blog/:slug',
        parse: { layoutPreview: (value: string) => value || undefined },
      },
      Landing: 'landing',
      // CORE-1156 — реферальная ссылка. Адрес выдаёт бэкенд
      // (`ReferralController.LinkUrl` = `{frontend}/r/{code}`), и без записи
      // здесь React Navigation молча уводил гостя на главную, не засчитав
      // переход: ссылка выглядела рабочей, а связи не возникало.
      // Вне гейта денег (CORE-1157) сознательно — экран ничего не показывает,
      // а переход с телефона засчитывать нужно так же.
      ReferralLanding: 'r/:code',
      Login: {
        path: 'login',
        parse: { returnTo: (v: string) => v || undefined },
      },
      Register: {
        path: 'register',
        parse: { returnTo: (v: string) => v || undefined },
      },
      ConfirmEmail: 'confirm-email',
      ForgotPassword: 'forgot-password',
      ResetPassword: 'reset-password',
      Dashboard: 'dashboard',
      Me: 'me',
      Masters: 'masters',
      MasterDetail: {
        path: 'masters/:masterId',
        parse: {
          masterId: (masterId: string) => Number(masterId),
        },
      },
      MasterSubscriptions: 'subscriptions',
      // CORE-929 — покупка программы. Путь вложен в программу, а не в профиль
      // мастера: ссылку из письма об оплате открывают не с витрины.
      //
      // CORE-1157 — путей нет в мобильном билде: экраны там не зарегистрированы,
      // а описанный путь без экрана — это ссылка, которая открывает приложение и
      // оставляет человека на пустом месте. Ссылка из письма на телефоне уходит
      // в браузер, где программы работают.
      ...(PROGRAMS_SURFACE_ENABLED ? {
        ProgramPurchase: {
          path: 'programs/:programId/purchase',
          parse: { programId: (programId: string) => Number(programId) },
        },
        // CORE-928 — публичная страница программы. Короткий путь без суффикса:
        // это и есть ссылка, которой делятся.
        ProgramPage: {
          path: 'programs/:programId',
          parse: { programId: (programId: string) => Number(programId) },
        },
      } : {}),
      AgapeCenter: {
        path: 'center',
        parse: { initialTab: (tab: string) => tab || undefined },
      },
      Lives: {
        path: 'lives',
        parse: {
          eventId: (id: string) => id ? Number(id) : undefined,
          date: (date: string) => date || undefined,
        },
      },
      Schedule: {
        path: 'schedule',
        parse: { eventId: (id: string) => id ? Number(id) : undefined },
      },
      Upcoming: {
        path: 'upcoming',
        parse: { eventId: (id: string) => id ? Number(id) : undefined },
      },
      Practices: 'practices',
      PracticeDetail: {
        path: 'practices/:id',
        parse: {
          id: (id: string) => Number(id),
          type: (type: string) => type || 'practice',
          masterProfileId: (masterProfileId: string) => masterProfileId ? Number(masterProfileId) : undefined,
        },
      },
      LiveDetail: {
        path: 'live/:id',
        // CORE-883 (QA 23.10) — `/lives/{id}` раздаёт бэкенд: так собраны
        // ссылки уведомлений об эфирах программ, по которым push открывает
        // вкладку (`sw.js` → `data.url`), напоминание о переносе, превью
        // ко-хоста. Маршрута с таким адресом не было, и ссылка уводила на
        // главную. Псевдоним нужен и после правки бэкенда: старые строки
        // ленты и уже доставленные push несут прежний адрес. Query
        // (`preview`, `invite`, `programId`) разбирается так же, как у
        // `/live/{id}`; ссылки, которые раздаёт сам фронт, остаются `/live/`.
        alias: ['lives/:id'],
        parse: {
          id: (id: string) => Number(id),
          masterProfileId: (masterProfileId: string) => masterProfileId ? Number(masterProfileId) : undefined,
          // CORE-883 — программа сессии: на 404 экран ведёт в неё, а не в тупик.
          programId: (programId: string) => programId ? Number(programId) : undefined,
        },
      },
      Chats: {
        path: 'chats',
        parse: {
          masterId: (masterId: string) => masterId ? Number(masterId) : undefined,
          conversationId: (conversationId: string) => conversationId ? Number(conversationId) : undefined,
        },
      },
      // CORE-630 — `/community` is registered unconditionally. It used to sit
      // inside the CIRCLES_ENABLED block below and so resolved to nothing:
      // the URL fell through to the landing page, which is what the QA re-test
      // caught. The screen behind it («Сообщество») does not depend on circles
      // — its two sections come from `/api/user/public` and link to public
      // profiles — so gating the route on the circles flag was collateral
      // damage, not a decision. The circles surface itself stays hidden.
      Community: 'community',
      // AMB-228 — the CircleChat linking entry is gated by the flag. With it
      // off, a direct URL like `/circle/1` no longer resolves to a working
      // screen and falls through to the navigator's not-found path.
      ...(CIRCLES_ENABLED ? {
        CircleChat: {
          path: 'circle/:circleId',
          parse: {
            // AMB-57 push deep-links carry the BE Circle.Id (GUID), while older
            // in-app callers pass the legacy numeric id. Keep the original form
            // when it isn't a plain integer so the screen lands correctly for
            // both shapes — CircleChatScreen treats circleId opaquely below.
            circleId: (circleId: string) =>
              /^\d+$/.test(circleId) ? Number(circleId) : (circleId as unknown as number),
          },
        },
      } : {}),
      // CORE-630 — must precede PublicUserProfile: `u/:userId` would otherwise
      // swallow the two extra segments and the post id would be lost.
      PublicUserPost: {
        path: 'u/:userId/post/:postId',
        parse: { postId: (postId: string) => Number(postId) },
      },
      PublicUserProfile: {
        path: 'u/:userId',
        // CORE-274 — `?tab=lightPath` deep-links a shared profile to «Путь Света».
        parse: { tab: (tab: string) => tab || undefined },
      },
      // CORE-966 — the gamification share link. Same path a crawler fetches for
      // the Open Graph card, so the address a person is sent and the address
      // that produced the preview they clicked are one and the same.
      GamificationShare: {
        path: 's/:token',
      },
      About: 'about',
      // CORE-1548 — адрес из QR на главной: сам уводит в нужный стор.
      Download: 'download',
      AboutPlatform: 'about-platform',
      Environment: 'environment',
      Support: 'support',
      Contact: 'contact',
      // CORE-1157 — денежные пути только там, где есть денежные экраны.
      ...(MONEY_SURFACE_ENABLED ? {
        MasterSales: 'master-sales',
        Finance: 'finance',
        // CORE-1743 — постоянный адрес документов мастера: его ставит сервер в
        // уведомление об одобрении заявки и передаёт продукт юристам.
        FinanceDocuments: 'finance/documents',
        // CORE-1534 — оплата подписки; сюда же возвращает платёжная страница.
        AgapellaSubscription: {
          path: 'finance/subscription',
          // CORE-1537 — сюда приложение открывает вкладку оплаты. Отдельный
          // путь затем, что сам `finance/subscription` Android перехватывает
          // как возврат с оплаты (App Links).
          alias: ['finance/subscription/pay'],
          parse: { purchase: (purchase: string) => Number(purchase) },
        },
      } : {
        // CORE-1537 — денежных экранов в мобилке нет, а подписка живёт карточкой
        // в «Настройках» (CORE-1535). Возврат с платёжной страницы (App Link или
        // `agapella://`) ведём туда: карточка покажет исход покупки.
        Settings: {
          path: 'finance/subscription',
          parse: { purchase: (purchase: string) => Number(purchase) },
        },
      }),
      MasterAnalytics: 'master-analytics',
      Notifications: 'notifications',
      NotificationSettings: 'notification-settings',
      MyCalendar: 'my-calendar',
      CreateEvent: 'events/create',
      ...(MONEY_SURFACE_ENABLED ? {
        Fund: 'fund',
        FundDistribution: 'fund/distribution',
        HowItWorks: 'fund/how-it-works',
        FundTransparency: 'fund/transparency',
        ReferralCredits: 'fund/credits',
        PayoutDetails: 'fund/payout-details',
        FundOnboarding: 'fund/onboarding',
        MasterWallet: 'wallet',
      } : {}),
      Docs: {
        path: 'docs/:docId?',
        parse: { docId: (v: string) => v || undefined },
      },
      LegalDoc: {
        path: 'legal/:docId',
      },
      CustDev: 'custdev',
      // CORE-901 — hidden gamification QA-preview; the screen itself gates access.
      GamificationPreview: 'qa/gamification',
      SoundLibrary: 'sound-library',
      MyLibrary: 'my-library',
      MemoryFacts: 'memory-facts',
      PracticeRooms: 'practice-rooms',
      CreateLiveSession: 'live-session/create',
      LiveSessionEntry: 'live-session/join/:roomId',
      LiveRoomSession: 'practice-rooms/session/:roomId',
      RoomAnnouncement: 'room/:roomId',
      AgapePractice: 'agape-practice',
      // Group D: /feed deep link redirects to dashboard's feed tab.
      // The standalone Feed screen is kept registered (below) for legacy
      // in-app navigation calls, but external URLs take the redirect path
      // via the listener in AppContent below.
      GuestRoom: 'guest-room/:liveId',
    },
  },
  // CORE-1579 — «назад» браузера на незнакомый адрес ронял приложение. Только
  // веб: на телефоне незнакомую ссылку библиотека и так пропускает, а главная
  // вместо неё увела бы человека с открытого экрана.
  ...(Platform.OS === 'web' ? { getStateFromPath: withHomeFallback(getStateFromPath) } : {}),
};

// Native hard auth gate: the only screens an unauthenticated user may see are
// the auth flow itself — plus, since CORE-1205 (owner, 15.09.2026), a Live
// session opened by link (`navigation/guestLiveAccess`). Everything else (incl.
// deep-link targets) bounces to Login. Web keeps its softer AuthCTA gate, so
// this applies on native only.
//
// `LegalDoc` is public by store requirement (CORE-447): Google Play's Data
// safety declaration points at /legal/delete-account, and a reviewer — like any
// user who has already lost access to their account — must be able to open it
// without signing in. The legal texts carry no personal data.
const AUTH_PUBLIC_ROUTES = new Set([
  'Login', 'Register', 'ConfirmEmail', 'ForgotPassword', 'ResetPassword',
  'LegalDoc',
]);

function AppContent() {
  const { isDark, colors: themeColors } = useTheme();
  const { isAuthenticated, isLoading, user, impersonation } = useAuth();
  const navRef = navigationRef;

  // Native hard auth gate. If a guest lands on any protected screen — including
  // one reached via a deep link — stash the intended target and reset to Login;
  // useLogin resumes it after sign-in. Runs on every navigation state change and
  // whenever auth/loading settles.
  const enforceAuthGate = useCallback(() => {
    if (Platform.OS === 'web') return;
    if (isLoading || isAuthenticated || !navRef.isReady()) return;
    const route = navRef.getCurrentRoute();
    if (!route || AUTH_PUBLIC_ROUTES.has(route.name)) return;
    // CORE-1205 — решение владельца 15.09.2026: по ссылке без входа можно в
    // Live-сессию, и только в неё (`navigation/guestLiveAccess`).
    const guestAccess = resolveGuestRouteAccess(route);
    if (guestAccess.kind === 'allow') return;
    if (guestAccess.kind === 'entry') {
      navRef.reset({ index: 0, routes: [{ name: 'LiveSessionEntry', params: { roomId: guestAccess.roomId } }] });
      return;
    }
    setPendingRoute({ name: route.name, params: route.params as object | undefined });
    navRef.reset({ index: 0, routes: [{ name: 'Login' }] });
  }, [isAuthenticated, isLoading, navRef]);

  useEffect(() => { enforceAuthGate(); }, [enforceAuthGate]);

  // CORE-1137 — с какой рекламы/органики пришёл этот визит. Намеренно НЕ ждёт
  // авторизации: событие описывает анонимный приход, а адрес к моменту, когда
  // авторизация уляжется, React Navigation уже перепишет.
  useAcquisitionAttribution();

  // CORE-1142 — внешний маркетинговый слой (Метрика / GA / пиксель). Ничего
  // не грузит, пока не выполнены все четыре условия: слой настроен, окружение
  // продовое, согласие дано, регион известен.
  // CORE-1404 — подросткам (14–17) счётчики не грузятся независимо от согласия.
  useMarketingTags(user?.ageGroup);
  // CORE-1404 — решение по cookie вошедшего пользователя хранится и на сервере.
  useCookieConsentSync(user?.id, user?.cookieConsent);

  // CORE-788 — if this app start came from a shared link (`?ref=share`), report
  // it once, after auth settles.
  useShareLinkAttribution(isAuthenticated, isLoading);

  // CORE-1753 — старт и возвращение из фона сообщаются chat-api: по ним
  // Агапелла пишет первой, не дожидаясь, пока человек откроет её чат.
  useAppOpenedBeacon(user?.id, isAuthenticated, isLoading, !!impersonation);

  // CORE-1715 — без переключателя языка (веб и приложение) интерфейс русский.
  useLanguageSwitcherGate();

  // On every navigation change: run the auth gate, then record the current
  // screen for Agapella page-context grounding (see services/chat/pageContext).
  const handleNavStateChange = useCallback(() => {
    enforceAuthGate();
    if (navRef.isReady()) noteRoute(navRef.getCurrentRoute());
    // CORE-1142 — счётчики считают просмотр в момент загрузки страницы, а она
    // здесь ровно одна: без этого вся навигация после входа выглядела бы
    // в Метрике одним бесконечным просмотром посадочной.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      marketingTags.pageView(window.location.pathname);
    }
  }, [enforceAuthGate, navRef]);

  // CORE-495 — re-title the tab when the user switches language. React
  // Navigation only recomputes `documentTitle` on a navigation/options change,
  // so without this the tab keeps the previous language until the next screen.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const retitle = () => {
      if (!navRef.isReady()) return;
      document.title = documentTitle((k) => i18n.t(k), navRef.getCurrentOptions(), navRef.getCurrentRoute());
    };
    i18n.on('languageChanged', retitle);
    return () => { i18n.off('languageChanged', retitle); };
  }, [navRef]);

  // URL-псевдонимы (/feed → /dashboard, /calendar → /schedule) для входящих
  // адресов. Один раз на монтировании: покрывает прямой заход по ссылке.
  //
  // Незнакомый адрес React Navigation не отвергает — он показывает начальный
  // экран и переписывает адрес на «/». Человек оказывается на главной, думая,
  // что открыл страницу; контекст, который уходит Agapella, описывает главную
  // — и выглядит это как враньё контекста, хотя увели самого человека.
  // Правила лежат в navigation/pathAliases, чтобы следующий такой адрес был
  // одной строкой и с тестом.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const alias = resolvePathAlias(window.location?.pathname);
    if (!alias) return;
    // CORE-1266 — назначение, адресуемое #фрагментом, отдаём настоящим
    // редиректом. Внутренним переходом туда не попасть: фрагмент не часть
    // состояния навигатора, и React Navigation стирает его собственной
    // записью адреса. А прямой заход по такому адресу приложение разбирает
    // правильно и давно — им и заканчиваем.
    if (alias.kind === 'redirect') {
      window.location.replace(alias.to);
      return;
    }
    // Переписываем адрес, чтобы «назад» не возвращал на несуществующий путь.
    window.history.replaceState(null, '', alias.path);
    // CORE-1266 — ждём готовности навигатора, а не одного тика.
    //
    // Здесь стоял `setTimeout(…, 0)`, и на прямом заходе по ссылке он попадал
    // в промежуток, когда навигатора ещё нет вовсе: пока восстанавливается
    // сохранённая сессия, `AppContent` держит заставку и `NavigationContainer`
    // не смонтирован. Переход молча терялся — адрес был уже переписан на
    // `/dashboard`, и человек оставался на вкладке по умолчанию. Отсюда и
    // «через раз»: успевал ли тик до готовности, решала скорость ответа
    // авторизации.
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attemptsLeft = 100; // ≈5 c — дольше ждать нечего, экран уже показан.
    const apply = () => {
      if (navRef.isReady()) {
        (navRef.current as any).navigate(alias.route, alias.params);
        return;
      }
      if (attemptsLeft-- <= 0) return;
      timer = setTimeout(apply, 50);
    };
    apply();
    return () => { if (timer) clearTimeout(timer); };
  }, [navRef]);

  // Handle push notification taps (service worker → app navigation)
  useEffect(() => {
    if (Platform.OS !== 'web' || !('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'push-received') return;
      const url: string = event.data.payload?.url || '';
      // Handle room_reminder or direct room URLs
      const roomMatch = url.match(/practice-rooms\/session\/([^/?#]+)/);
      if (roomMatch && navRef.current) {
        const roomId = roomMatch[1];
        navRef.current.navigate('LiveRoomSession', {
          roomId,
          roomTitle: event.data.payload?.title || 'Practice Room',
        });
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navRef]);

  // CORE-639 — shell background is transparent on web (pageBg) so the global
  // background layer (body::before) shows through; native keeps its opaque
  // canvas fill (pageBg === canvas on native).
  const navigationTheme = isDark
    ? { ...DarkTheme, colors: { ...DarkTheme.colors, background: themeColors.pageBg } }
    : { ...DefaultTheme, colors: { ...DefaultTheme.colors, background: themeColors.pageBg } };

  // Hold on a branded splash until the stored session resolves. Mounting the
  // navigator before that lets Home paint for a frame and then snap to Login —
  // the amateurish flash the gate alone can't avoid. Once auth settles we mount
  // the navigator already pointed at the right first screen (native only; web
  // keeps URL-driven routing + the softer AuthCTA gate).
  if (isLoading) {
    return (
      // CORE-1733 — заставка до восстановления сессии — на общем фоне (на вебе
      // `pageBg` прозрачен): без белой вспышки перед сливочной страницей.
      <View style={{ flex: 1, backgroundColor: themeColors.pageBg }}>
        <ExpoStatusBar style={isDark ? 'light' : 'dark'} />
        <GlobalPageLoader />
      </View>
    );
  }

  const initialRouteName = Platform.OS !== 'web' && !isAuthenticated ? 'Login' : 'Home';

  return (
    // CORE-1343 — удостоверение идущей сессии. Выше навигации, потому что его
    // читают обе стороны: маршрут комнаты объявляет сессию, слой над
    // навигацией её рисует — развёрнутой или плиткой в углу.
    <LiveSessionProvider>
    {/* CORE-1751 — окно рефлексии у корня: его открывают выход из Live-сессии
        (слой сессии над навигацией и экран комнаты на вебе) и кнопка в
        сообщении Агапеллы (чат, шторка, виджет). Выше поздравлений по Пути
        Света: они ждут, пока окно рефлексии на экране («сначала рефлексия»). */}
    <ReflectionLauncherProvider>
    <GamificationCelebrationProvider>
    <OnboardingTourProvider>
      <ExpoStatusBar style={isDark ? 'light' : 'dark'} />
      <ImpersonationBanner />
      <NavigationContainer
        ref={navRef}
        linking={linking}
        onReady={handleNavStateChange}
        onStateChange={handleNavStateChange}
        theme={navigationTheme}
        documentTitle={{
          // CORE-495 — the browser tab follows the UI language. See
          // src/i18n/routeTitles.ts for the route → key table.
          formatter: (options, route) => documentTitle((k) => i18n.t(k), options, route),
        }}
      >
        <AuthCTAProvider>
          <ReportProvider>
          <RepostProvider>
          {/* CORE-638 — the routes are React.lazy chunks; this boundary covers
              the fetch of a screen the visitor opens for the first time. It
              renders on the page background so the switch reads as a beat of
              loading, not as a flash of empty white. */}
          <React.Suspense
            fallback={<GlobalPageLoader />}
          >
          <Stack.Navigator
            initialRouteName={initialRouteName}
            screenOptions={{
              headerShown: false,
              contentStyle: { backgroundColor: themeColors.pageBg },
              // Instant page switches — the tester found the slide transition
              // long/janky. Modals (slide_from_bottom) + overlays (fade) keep
              // their own animation via per-screen options.
              animation: 'none',
            }}
          >
            <Stack.Screen name="Home" component={HomeScreen} />
            <Stack.Screen name="News" component={NewsScreen} />
            <Stack.Screen name="NewsArticle" component={NewsArticleScreen} />
            <Stack.Screen name="Landing" component={LandingScreen} />
            <Stack.Screen name="ReferralLanding" component={ReferralLandingScreen} />
            <Stack.Screen
              name="Login"
              component={LoginScreen}
              options={{
                presentation: 'transparentModal',
                animation: 'fade',
              }}
            />
            <Stack.Screen
              name="Register"
              component={RegisterScreen}
              options={{
                presentation: 'transparentModal',
                animation: 'fade',
              }}
            />
            <Stack.Screen
              name="ConfirmEmail"
              component={ConfirmEmailScreen}
              options={{
                animation: 'fade',
              }}
            />
            <Stack.Screen
              name="ForgotPassword"
              component={ForgotPasswordScreen}
              options={{
                presentation: 'transparentModal',
                animation: 'fade',
              }}
            />
            <Stack.Screen
              name="ResetPassword"
              component={ResetPasswordScreen}
              options={{
                presentation: 'transparentModal',
                animation: 'fade',
              }}
            />
            <Stack.Screen
              name="Dashboard"
              component={DashboardScreen as React.ComponentType<any>}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Me"
              component={DashboardScreen as React.ComponentType<any>}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Masters"
              component={MastersScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="MasterDetail"
              component={MasterDetailScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="MasterSubscriptions"
              component={MasterSubscriptionsScreen}
              options={{
                animation: 'none',
              }}
            />
            {/* CORE-1157 — в мобильном билде экранов программы нет: ни витрины,
                ни покупки. Роут, а не только кнопка: иначе на страницу можно
                попасть уведомлением или ссылкой, минуя скрытые точки входа. */}
            {PROGRAMS_SURFACE_ENABLED && <Stack.Screen
              name="ProgramPurchase"
              component={ProgramPurchaseScreen}
              options={{
                animation: 'none',
              }}
            />}
            {PROGRAMS_SURFACE_ENABLED && <Stack.Screen
              name="ProgramPage"
              component={ProgramPageScreen}
              options={{
                animation: 'none',
              }}
            />}
            <Stack.Screen
              name="AgapeCenter"
              component={AgapeCenterScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Lives"
              component={LivesScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="EventsCalendar"
              component={EventsCalendarScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="EventsAll"
              component={EventsAllScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="EventsFilters"
              component={EventsFiltersScreen}
              options={{ animation: 'slide_from_bottom' }}
            />
            <Stack.Screen
              name="EventDetail"
              component={EventDetailScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="CreateEvent"
              component={CreateEventScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="EventParticipants"
              component={EventParticipantsScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="Schedule"
              component={LivesScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Upcoming"
              component={LivesScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Practices"
              component={PracticesScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="PracticeDetail"
              component={PracticeDetailScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="UploadPractice"
              component={UploadPracticeScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="MyCollection"
              component={MyCollectionScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="Settings"
              component={SettingsScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="LiveDetail"
              // CORE-528 — LiveDetailRoute picks between the announcement page
              // and the cohost room from `?preview=`. It must be the registered
              // component (not LiveDetailScreen) or invite links land on the
              // event pre-registration page.
              component={LiveDetailRoute}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="LiveBroadcast"
              component={LiveBroadcastScreen}
              options={{ animation: 'none' }}
            />
            <Stack.Screen
              name="Chats"
              component={ChatsScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="ChatThread"
              component={ChatThreadScreen}
              options={{ animation: 'none' }}
            />
            {/* CORE-630 — registered unconditionally so `/community` opens the
                object instead of falling through to the landing page. Not in
                the top nav: making the page reachable by URL is routing, and
                putting it back in the menu is a product call (AMB-189). */}
            <Stack.Screen
              name="Community"
              component={CommunityScreen}
              options={{
                animation: 'none',
              }}
            />
            {/* AMB-228 — Circle / EventThread stack entries are conditionally
                registered. With CIRCLES_ENABLED off, the navigator doesn't
                know about them; deep-links land on the not-found path. */}
            <Stack.Screen
              name="PublicUserProfile"
              component={PublicUserProfileScreen}
              options={{
                animation: 'none',
              }}
            />
            {/* CORE-630 — same shim component, entered with a post id. */}
            <Stack.Screen
              name="PublicUserPost"
              component={PublicUserProfileScreen}
              options={{
                animation: 'none',
              }}
            />
            {/* CORE-966 — `/s/:token` resolves the shared card, then renders the
                owner's «Путь Света» exactly as /u/:userId?tab=lightPath does. */}
            <Stack.Screen
              name="GamificationShare"
              component={GamificationShareLinkRoute}
              options={{
                animation: 'none',
              }}
            />
            {CIRCLES_ENABLED && <Stack.Screen
              name="CircleChat"
              component={CircleChatScreen}
              options={{
                animation: 'none',
              }}
            />}
            <Stack.Screen
              name="About"
              component={AboutScreen}
              options={{
                animation: 'none',
              }}
            />
            {/* CORE-1548 — страница из QR: уводит в стор по системе телефона. */}
            <Stack.Screen
              name="Download"
              component={DownloadScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="AboutPlatform"
              component={AboutPlatformScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Environment"
              component={EnvironmentScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Support"
              component={SupportScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="Contact"
              component={ContactScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="NotificationSettings"
              component={NotificationSettingsScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="SecuritySettings"
              component={SecuritySettingsScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="BlockedUsers"
              component={BlockedUsersScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="MyCalendar"
              component={MyCalendarScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="MyLibrary"
              component={MyLibraryScreen}
              options={{
                animation: 'none',
              }}
            />
            <Stack.Screen
              name="MemoryFacts"
              component={MemoryFactsScreen}
              options={{
                animation: 'none',
              }}
            />
            {/* CORE-1157 — денежный контур целиком вне мобильного билда:
                «Финансы», «Фонд» и всё, что из них растёт (распределение,
                прозрачность, реферальные начисления, реквизиты выплат, кошелёк
                и продажи мастера). «Аналитика» осталась: там просмотры и
                подписчики, денег нет. */}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="MasterSales" component={MasterSalesScreen} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="Finance" component={FinanceScreen} options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="FinanceDocuments" component={FinanceDocumentsScreen} options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="AgapellaSubscription" component={AgapellaSubscriptionScreen} options={{ animation: 'none' }} />}
            <Stack.Screen name="MasterAnalytics" component={MasterAnalyticsScreen} />
            <Stack.Screen name="Notifications" component={NotificationsScreen} />
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="Fund" component={FundScreen}
              options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="FundDistribution" component={FundDistributionScreen}
              options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="HowItWorks" component={HowItWorksScreen}
              options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="FundTransparency" component={FundTransparencyScreen}
              options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="ReferralCredits" component={ReferralCreditsScreen}
              options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="PayoutDetails" component={PayoutDetailsScreen}
              options={{ animation: 'none' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="FundOnboarding" component={FundOnboardingScreen}
              options={{ animation: 'slide_from_bottom', presentation: 'modal' }} />}
            {MONEY_SURFACE_ENABLED && <Stack.Screen name="MasterWallet" component={MasterWalletScreen}
              options={{ animation: 'none' }} />}
            <Stack.Screen name="Docs" component={DocsScreen}
              options={{ animation: 'none' }} />
            <Stack.Screen name="LegalDoc" component={LegalDocScreen}
              options={{ animation: 'none' }} />
            <Stack.Screen name="CustDev" component={CustDevScreen}
              options={{ animation: 'slide_from_bottom' }} />
            <Stack.Screen name="SoundLibrary" component={SoundLibraryScreen}
              options={{ animation: 'none' }} />
            {/* CORE-901 — hidden gamification QA-preview; access gated inside the screen. */}
            <Stack.Screen name="GamificationPreview" component={GamificationPreviewScreen}
              options={{ animation: 'none' }} />
            <Stack.Screen name="PracticeRooms" component={PracticeRoomsScreen}
              options={{ animation: 'none' }} />
            <Stack.Screen name="CreateLiveSession" component={CreateLiveSessionScreen}
              options={{ animation: 'slide_from_bottom' }} />
            <Stack.Screen name="LiveSessionEntry" component={LiveSessionEntryScreen}
              options={{ animation: 'slide_from_bottom' }} />
            {/* CORE-1425 — на iOS комната обычным экраном, не модалкой. Комнату
                рисует слой LiveSessionHost после NavigationContainer (CORE-1343),
                а iOS показывает fullScreenModal отдельным контроллером поверх
                корневого вида — слой пропадал, оставался чёрный фон заглушки.
                'card' явно: маршрут без presentation над модалкой native-stack
                сам делает модалкой. Android модалку и так кладёт в стек. */}
            <Stack.Screen name="LiveRoomSession" component={LiveRoomScreen}
              options={{
                animation: 'slide_from_bottom',
                presentation: Platform.OS === 'ios' ? 'card' : 'fullScreenModal',
              }} />
            <Stack.Screen name="RoomAnnouncement" component={RoomAnnouncementScreen}
              options={{ animation: 'none' }} />
            <Stack.Screen name="AgapePractice" component={AgapePracticeScreen}
              options={{ animation: 'slide_from_bottom' }} />
            {CIRCLES_ENABLED && <Stack.Screen name="EventThreadScreen" component={EventThreadScreen}
              options={{ animation: 'none' }} />}
            {/* CORE-1114 (B-6) — обсуждение практики в приложении; веб показывает тред на самой странице практики. */}
            {Platform.OS !== 'web' && <Stack.Screen name="PracticeDiscussion" component={PracticeDiscussionScreen}
              options={{ animation: 'slide_from_right' }} />}
            <Stack.Screen name="Feed" component={FeedScreen}
              options={{ animation: 'none' }} />
            <Stack.Screen name="GuestRoom" component={GuestRoomScreen as React.ComponentType<any>}
              options={{ animation: 'slide_from_bottom', presentation: 'fullScreenModal' }} />

          </Stack.Navigator>
          </React.Suspense>
          </RepostProvider>
          </ReportProvider>
        </AuthCTAProvider>
      </NavigationContainer>
      {/* CORE-1343 — свёрнутая Live-сессия. Слой стоит НАД навигацией и рядом
          с ней, а не внутри: экран, размонтируясь, уносил с собой RTC, поэтому
          свернуть сессию было некуда — она умирала вместе с экраном. */}
      <LiveSessionHost />
      <OnboardingProfileGate />
      {/* CORE-1538 — оффер пробного доступа после знакомства и окна о его конце.
          Пусто, пока сервер не включил подписку этому человеку. */}
      <AgapellaTrialGate />
      {/* CORE-1776 — окно «Входит в Agapēlla»: экраны зовут openAgapellaPaywall. */}
      <AgapellaPaywallHost />
      {/* CORE-1741 — картинка из чата закрытой сессии во весь экран (приложение). */}
      <ChatImageViewerHost />
      {/* CORE-496 — home-page onboarding spotlight (web only). Dim + ring only:
          the dialogue itself is spoken by Agapella in the widget below. */}
      {ONBOARDING_TOUR_ENABLED && <OnboardingTourOverlay />}
      {/* CORE-498 / CORE-499 — persistent Agapella launcher + compact widget (web
          only), and the surface the tour speaks through — so it must be mounted
          whenever the tour is on, even if the standalone widget isn't. */}
      {(AGAPELLA_WIDGET_ENABLED || ONBOARDING_TOUR_ENABLED) && <AgapellaWidget />}
      {/* CORE-509 — the detailed Путь света screens. Mounted at the root, not in
          the profile: a level-up is a platform-wide event, and living only on
          one page meant most of them were met by nothing at all. Opens only
          when the user takes Agapella up on «Посмотреть». */}
      {/* CORE-858 — root SafeAreaProvider covers this host even though it lives
          after </NavigationContainer>. The absolute wrapper claims no layout and
          swallows no taps; the modals render out of it. */}
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <GamificationCelebrationHost />
      </View>
      {/* CORE-1142 — запрос согласия на маркетинговые cookie. Рендерится только
          там, где внешний слой вообще может включиться, и его ответ — гейт
          перед загрузкой любого стороннего счётчика. */}
      <CookieBanner />
      {/* CORE-1659 — выбор по категориям cookie: открывается из баннера, футера и настроек. */}
      <CookiePreferencesModal />
      {/* CORE-1404 — обязательный экран «возраст + согласие» для аккаунта без них
          (новый аккаунт через Google/Apple). */}
      <ConsentGate />
      {/* CORE-413: dev-only runtime env selector. Renders null in prod builds. */}
      <EnvSwitcher />
    </OnboardingTourProvider>
    </GamificationCelebrationProvider>
    </ReflectionLauncherProvider>
    </LiveSessionProvider>
  );
}

function App() {
  // Lora + Nunito Sans для нативных экранов. Рендер не блокируем: до готовности
  // текст рисуется системным шрифтом, а не пустым экраном. На вебе no-op —
  // там шрифты приходят CSS-переменными.
  useAgapellaFonts();

  return (
    <I18nextProvider i18n={i18n}>
      {/* CORE-1391 — без прогрева клавиатуры. По умолчанию провайдер на старте
          создаёт скрытый UITextField и сразу снимает с него фокус; на iOS
          `resignFirstResponder` ждёт, пока клавиатура доинициализируется, и
          главный поток стоял 2.8–3.6 с (Sentry REACT-NATIVE-4E, через 2 с после
          запуска, без касаний). Первое открытие клавиатуры станет чуть медленнее,
          но уже по нажатию, а не блокировкой всего приложения. */}
      <KeyboardProvider preload={false}>
      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <AuthProvider>
        <BlockedUsersProvider>
        <CalendarProvider>
          <SavedEventsProvider>
            <ThemeProvider>
              <FundProvider>
                <ErrorBoundary>
                  {/* CORE-607 — contacts load on auth, independent of Stream.
                      CORE-618 — StreamChatProvider removed: every chat surface
                      now rides Agape.ChatApi. */}
                  <ChatContactsProvider>
                    <EventsFilterProvider>
                      <AppContent />
                    </EventsFilterProvider>
                    {/* In-app chime on incoming messages (foreground). */}
                    <MessageNotifier />
                    {/* CORE-1457 — тосты, которые должны пережить уход с экрана (web). */}
                    <FlashNoticeHost />
                    {/* Native push: registers the Expo token + routes taps (no-op on web). */}
                    <PushNotifications />
                    {/* Sits idle until a dynamic-import catch trips
                        triggerStaleBundle(); web-only. */}
                    <StaleBundleBanner />
                    {/* CORE-2098 — вход истёк: спокойно предложить войти снова (web). */}
                    <SessionExpiredPrompt />
                  </ChatContactsProvider>
                </ErrorBoundary>
              </FundProvider>
            </ThemeProvider>
          </SavedEventsProvider>
        </CalendarProvider>
        </BlockedUsersProvider>
      </AuthProvider>
      </SafeAreaProvider>
      </KeyboardProvider>
    </I18nextProvider>
  );
}

// Sentry.wrap installs an ErrorBoundary at the root + integrates with
// the native crash handler / touch-event breadcrumbs. Returns the same
// component shape, so the export contract is unchanged.
export default Sentry.wrap(App);
